import { create } from "zustand"
import type { Lane } from "./present"
import {
  DEFAULT_CONTEXT,
  EMPTY_ANNOTATIONS,
  type NiraAnnotations,
  type NiraAnswer,
  type NiraAuthority,
  type NiraBrief,
  type NiraContext,
  type NiraLane,
  type NiraMessage,
  type NiraSearchStat,
  type NiraThread,
  type NiraThreadSummary,
  type NiraTurn,
} from "./types"

export type Screen = "intake" | "search" | "board" | "auth"

export interface LiveBoard {
  authorities: NiraAuthority[]
  set_aside: NiraAuthority[]
  law?: NiraAuthority[]
  lane_counts: Partial<Record<NiraLane, number>>
  jurisdiction_note: string
}

/** The turn being answered, built from its events. */
export interface LiveTurn {
  threadId: string
  kind: "research" | "discuss"
  userMessage: NiraMessage
  startedAt: number
  phase: string
  phaseAt: Record<string, number>
  brief: NiraBrief | null
  queries: string[]
  searches: NiraSearchStat[]
  cards: NiraAuthority[]
  board: LiveBoard | null
  answer: NiraAnswer | null
  error: string | null
}

export type NavRequest = { kind: "new" } | { kind: "open"; id: string } | null

interface NiraState {
  threads: NiraThreadSummary[]
  threadsLoaded: boolean
  activeId: string | null
  thread: NiraThread | null
  loadingThread: boolean
  live: LiveTurn | null
  context: NiraContext
  screen: Screen
  focus: { messageId: string | null; ref: string | null }
  laneFilter: Lane | "all"
  facts: string
  followDraft: string
  trayOpen: boolean
  notice: string | null
  navRequest: NavRequest

  setThreads: (threads: NiraThreadSummary[]) => void
  upsertThread: (summary: NiraThreadSummary) => void
  removeThread: (id: string) => void
  openThread: (thread: NiraThread | null, id?: string | null) => void
  setLoadingThread: (loading: boolean) => void
  setContext: (patch: Partial<NiraContext>) => void
  setScreen: (screen: Screen) => void
  openAuthority: (messageId: string | null, ref: string) => void
  setLaneFilter: (lane: Lane | "all") => void
  setFacts: (facts: string) => void
  setFollowDraft: (text: string) => void
  setTrayOpen: (open: boolean) => void
  setNotice: (notice: string | null) => void
  requestNav: (request: NavRequest) => void
  setAnnotations: (annotations: NiraAnnotations) => void
  startLive: (threadId: string, kind: LiveTurn["kind"], userMessage: NiraMessage) => void
  applyEvent: (type: string, payload: Record<string, unknown>) => void
  finishLive: (message: NiraMessage | null, summary: NiraThreadSummary | null) => void
  failLive: (error: string) => void
  clearLive: () => void
}

const STEP_ORDER = ["understanding", "searching", "reading", "weighing", "writing"]

export const useNiraStore = create<NiraState>((set, get) => ({
  threads: [],
  threadsLoaded: false,
  activeId: null,
  thread: null,
  loadingThread: false,
  live: null,
  context: DEFAULT_CONTEXT,
  screen: "intake",
  focus: { messageId: null, ref: null },
  laneFilter: "all",
  facts: "",
  followDraft: "",
  trayOpen: false,
  notice: null,
  navRequest: null,

  setThreads: (threads) => set({ threads, threadsLoaded: true }),
  upsertThread: (summary) =>
    set((s) => {
      const rest = s.threads.filter((t) => t.id !== summary.id)
      const thread = s.thread && s.thread.id === summary.id ? { ...s.thread, ...withoutMessages(summary) } : s.thread
      return { threads: [withoutMessages(summary), ...rest].sort((a, b) => b.updated_at.localeCompare(a.updated_at)), thread }
    }),
  removeThread: (id) =>
    set((s) => ({
      threads: s.threads.filter((t) => t.id !== id),
      ...(s.activeId === id ? { activeId: null, thread: null, screen: "intake" as Screen } : {}),
    })),
  openThread: (thread, id) =>
    set((s) => {
      const nextId = thread?.id ?? id ?? null
      const research = thread ? latestResearch(thread) : null
      const liveHere = s.live && s.live.threadId === nextId ? s.live : null
      return {
        thread,
        activeId: nextId,
        context: thread?.context ? { ...DEFAULT_CONTEXT, ...thread.context } : s.context,
        focus: { messageId: null, ref: null },
        laneFilter: "all",
        trayOpen: false,
        live: liveHere,
        screen: liveHere?.kind === "research" ? "search" : research ? "board" : thread ? "intake" : s.screen,
      }
    }),
  setLoadingThread: (loadingThread) => set({ loadingThread }),
  setContext: (patch) => set((s) => ({ context: { ...s.context, ...patch } })),
  setScreen: (screen) => set({ screen }),
  openAuthority: (messageId, ref) => set({ focus: { messageId, ref }, screen: "auth", trayOpen: false }),
  setLaneFilter: (laneFilter) => set({ laneFilter }),
  setFacts: (facts) => set({ facts }),
  setFollowDraft: (followDraft) => set({ followDraft }),
  setTrayOpen: (trayOpen) => set({ trayOpen }),
  setNotice: (notice) => set({ notice }),
  requestNav: (navRequest) => set({ navRequest }),
  setAnnotations: (annotations) =>
    set((s) => ({
      thread: s.thread ? { ...s.thread, annotations } : s.thread,
      threads: s.threads.map((t) => (t.id === s.activeId ? { ...t, annotations } : t)),
    })),

  startLive: (threadId, kind, userMessage) =>
    set({
      live: {
        threadId,
        kind,
        userMessage,
        startedAt: Date.now(),
        phase: "understanding",
        phaseAt: { understanding: Date.now() },
        brief: null,
        queries: [],
        searches: [],
        cards: [],
        board: null,
        answer: null,
        error: null,
      },
    }),

  applyEvent: (type, payload) => {
    const live = get().live
    if (!live) return
    const next: LiveTurn = { ...live }
    switch (type) {
      case "accepted": {
        const user = payload.user_message as NiraMessage | undefined
        if (user) next.userMessage = user
        break
      }
      case "status": {
        const phase = String(payload.phase ?? "")
        if (STEP_ORDER.includes(phase) && !(phase in live.phaseAt)) next.phaseAt = { ...live.phaseAt, [phase]: Date.now() }
        next.phase = phase
        if (Array.isArray(payload.queries)) next.queries = payload.queries.map(String)
        break
      }
      case "brief": {
        next.brief = payload.brief as NiraBrief
        if (next.brief.intent === "discuss") next.kind = "discuss"
        break
      }
      case "search":
        next.searches = [...live.searches, payload.search as NiraSearchStat]
        break
      case "authority":
        next.cards = [...live.cards, payload.authority as NiraAuthority].sort((a, b) => a.rank - b.rank)
        break
      case "board":
        next.board = payload as unknown as LiveBoard
        break
      case "answer":
        next.answer = payload.answer as NiraAnswer
        break
      default:
        return
    }
    set({ live: next })
  },

  finishLive: (message, summary) =>
    set((s) => {
      const live = s.live
      let thread = s.thread
      if (thread && live && message && thread.id === live.threadId) {
        const without = thread.messages.filter((m) => m.id !== live.userMessage.id && m.id !== message.id)
        thread = { ...thread, ...(summary ? withoutMessages(summary) : {}), messages: [...without, live.userMessage, message] }
      }
      const threads = summary ? [withoutMessages(summary), ...s.threads.filter((t) => t.id !== summary.id)] : s.threads
      const research = live?.kind === "research" && Boolean(message?.turn) && !message?.turn?.board_from_turn
      return {
        live: null,
        thread,
        threads,
        screen: research && s.screen === "search" ? "board" : s.screen,
        focus: research ? { messageId: null, ref: null } : s.focus,
      }
    }),

  failLive: (error) => set((s) => (s.live ? { live: { ...s.live, error } } : {})),
  clearLive: () => set({ live: null }),
}))

function withoutMessages(summary: NiraThreadSummary): NiraThreadSummary {
  const { messages: _ignored, ...fields } = summary as Partial<NiraThread>
  return fields as NiraThreadSummary
}

export interface ResearchTurn {
  messageId: string
  turn: NiraTurn
  facts: string
  followUps: Array<{ question: NiraMessage; answer: NiraMessage | null }>
}

/** The latest research turn of a thread, the officer's words that asked it,
 * and the discussion turns after it (its follow-ups). */
export function latestResearch(thread: NiraThread | null): ResearchTurn | null {
  if (!thread) return null
  const messages = thread.messages
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]
    if (m.role !== "assistant" || !m.turn || m.turn.board_from_turn) continue
    const question = messages.slice(0, i).reverse().find((x) => x.role === "user")
    const followUps: ResearchTurn["followUps"] = []
    for (let j = i + 1; j < messages.length; j += 1) {
      if (messages[j].role !== "user") continue
      const answer = messages[j + 1]?.role === "assistant" ? messages[j + 1] : null
      followUps.push({ question: messages[j], answer })
    }
    return { messageId: m.id, turn: m.turn, facts: question?.text ?? "", followUps }
  }
  return null
}

export function annotationsOf(thread: NiraThread | null): NiraAnnotations {
  return { ...EMPTY_ANNOTATIONS, ...(thread?.annotations ?? {}) }
}
